import React, { useContext } from 'react';
import { AppContext } from '../../App';
import { CircularProgress, LinearProgress, Typography } from '@mui/material';
import useFields from '../hooks/useFields';
import useGlynt from '../hooks/useGlynt';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DraggableProvided,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';
import { IField } from '../../types/my-types';
import { IGetFieldDataTypeAndConfig } from './utils/FieldTypes';
import { getUpdatedFieldData } from './utils/getUpdatedFieldData';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { refreshCertification } from '../../utils/certificationCache';

import errorResponses from '../../errorResponses';
export interface FieldsOrderProps {
  fieldOffSet?: number;
  trainingSetId: string;
  getFieldDataTypeAndConfig: IGetFieldDataTypeAndConfig;
}

function compareFields(a: IField, b: IField) {
  if (a.field_order < b.field_order) {
    return -1;
  }
  if (a.field_order > b.field_order) {
    return 1;
  }
  return 0;
}

const FieldsOrder: React.FC<FieldsOrderProps> = ({ trainingSetId, getFieldDataTypeAndConfig }) => {
  const { setToast } = useContext(AppContext);
  const {
    currentPage,
    totalPages,
    data: fieldsData,
  } = useFields(trainingSetId) as { currentPage: number; totalPages: number; data: IField[] };
  const cache = useQueryClient();
  const glynt = useGlynt();

  const [myFields, setMyFields] = React.useState<IField[]>([]);

  const [selectedFields, setSelectedFields] = React.useState<Array<{ label: string; id: string }>>(
    []
  );

  const [loading, setLoading] = React.useState(false);

  const orderField = useMutation({
    mutationFn: async ({ fieldId, fieldOrder }: { fieldId: string; fieldOrder: number }) => {
      let dataType;
      let dataTypeConfig;

      if (getFieldDataTypeAndConfig) {
        const result = getFieldDataTypeAndConfig(fieldId);
        dataType = result.dataType;
        dataTypeConfig = result.dataTypeConfig;
      }

      const updatedFieldData = getUpdatedFieldData({
        field_order: fieldOrder,
        fieldDataType: dataType,
        fieldDataTypeConfig: dataTypeConfig,
      });

      const response = (await glynt).fields.update(fieldId, updatedFieldData);
      return response;
    },

    onSuccess: (data, variables) => {
      refreshCertification(cache, trainingSetId);
      cache.invalidateQueries({
        queryKey: ['fields']
      });
      cache.invalidateQueries({
        queryKey: ['allFields']
      });
      setToast({ open: false });
      setToast({
        open: true,
        message: `Field order (${data.label}) updated successfully.`,
        variant: 'success',
      });
    },

    onError: (error) => {
      setToast({
        open: true,
        message: errorResponses.failedToReorderField,
        variant: 'error',
      });
    }
  });

  React.useEffect(() => {
    if (fieldsData?.length > 0) {
      let sortedFields = fieldsData.slice().sort(compareFields);
      if (JSON.stringify(sortedFields) !== JSON.stringify(myFields)) {
        setMyFields(sortedFields);
      }
    }
  }, [fieldsData]);

  if (fieldsData?.length === 0 || !myFields) {
    return <LinearProgress />;
  }

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    if (selectedFields.length === 0 || selectedFields.length === 1) {
      const newIndex = result.destination.index + 1;

      const tempFields = [...myFields];
      const [removed] = tempFields.splice(result.source.index, 1);
      tempFields.splice(result.destination.index, 0, removed);
      setMyFields(tempFields);

      setSelectedFields([]);

      try {
        await orderField.mutateAsync({
          fieldId: result.draggableId,
          fieldOrder: newIndex,
        });

        cache.setQueryData(['fields'], tempFields);
      } catch (error) {
        setMyFields(myFields);
      }
    } else {
      setLoading(true);

      const currentIndex = result.destination.index;
      const isDraggingDown = result.destination.index > result.source.index;

      for (let i = 0; i < selectedFields.length; i++) {
        const field = selectedFields[i];
        let newIndex;

        if (isDraggingDown) {
          newIndex = currentIndex + 1;
        } else {
          newIndex = currentIndex + i + 1;
        }

        await orderField.mutateAsync({
          fieldId: field.id,
          fieldOrder: newIndex,
        });
      }

      setToast({ open: false });
      setToast({
        open: true,
        message: `All fields have been updated!`,
        variant: 'success',
      });

      setLoading(false);
      setSelectedFields([]);
    }
  };

  const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? 'lightgray' : 'white',
    padding: grid,
    width: 250,
  });

  const grid = 8;

  const getItemStyle = (
    isDragging: boolean,
    draggableStyle: React.CSSProperties,
    label: string
  ) => ({
    userSelect: 'none' as React.CSSProperties['userSelect'],
    padding: '4px 25px 4px 3px',
    margin: `0 0 ${grid}px 0`,

    background: selectedFields.some((field) => field.label === label)
      ? 'gray'
      : isDragging
        ? 'rgb(102, 187, 169)'
        : 'white',

    border: '1px dashed black',

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const onClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.defaultPrevented) {
      return;
    }

    event.preventDefault();
    performAction(event);
  };

  // Determines if the multiSelect key was used
  const wasMultiSelectKeyUsed = (event: React.MouseEvent<HTMLDivElement>) => event.shiftKey;

  const performAction = (event: React.MouseEvent<HTMLDivElement>) => {
    const fieldLabel = (event.target as HTMLElement).getAttribute('data-label');
    const fieldId = (event.target as HTMLElement).getAttribute('data-field-id');

    if (!fieldLabel || !fieldId) {
      return;
    }

    if (wasMultiSelectKeyUsed(event)) {
      const fieldObject = { label: fieldLabel, id: fieldId };
      if (!selectedFields.some((field) => field.label === fieldLabel)) {
        setSelectedFields((prevSelectedFields) => {
          const updatedFields = [...prevSelectedFields, fieldObject];
          return updatedFields;
        });
      } else {
        setSelectedFields((prevSelectedFields) => {
          const updatedFields = prevSelectedFields.filter((field) => field.label !== fieldLabel);
          return updatedFields;
        });
      }
      return;
    }

    setSelectedFields([{ label: fieldLabel, id: fieldId }]);
  };

  if (!currentPage || currentPage !== totalPages) {
    return <LinearProgress />;
  }

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ backgroundColor: 'white' }}>
        <Typography>Use Drag and Drop to Reorder Fields. Saved automatically.</Typography>
      </div>
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '300px',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.4)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
          }}
        >
          <CircularProgress style={{ marginTop: '50px' }} />
        </div>
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
              data-cy="div-mark-fields-order"
            >
              {myFields.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      data-label={item.label}
                      data-field-id={item.id}
                      data-cy={`btn-field-reorder-${item.label}`}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided?.draggableProps?.style || {},
                        item.label
                      )}
                      onClick={(event: React.MouseEvent<HTMLDivElement>) => onClick(event)}
                    >
                      {index + 1}. {item.label}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default FieldsOrder;
